import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import educationService, { 
  School, 
  SchoolCreateRequest, 
  SchoolUpdateRequest, 
  CourseCreateRequest, 
  CourseUpdateRequest,
  CourseAnalysisRequest,
  SchoolsListResponse 
} from '@/services/educationService';
import ToastService from '@/utils/toast';

export const useEducationData = () => {
  const queryClient = useQueryClient();

  // Fetch user schools
  const {
    data: schoolsData,
    isLoading,
    error,
    refetch
  } = useQuery<SchoolsListResponse>({
    queryKey: ['education', 'schools'],
    queryFn: () => educationService.getUserSchools(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Temporarily disabled due to database schema issues
  });

  // Create school mutation
  const createSchoolMutation = useMutation({
    mutationFn: (schoolData: SchoolCreateRequest) => educationService.createSchool(schoolData),
    onSuccess: (newSchool) => {
      queryClient.invalidateQueries({ queryKey: ['education', 'schools'] });
      ToastService.showSuccess('School added successfully!');
    },
    onError: (error: any) => {
      console.error('Error creating school:', error);
      ToastService.showError('Failed to add school. Please try again.');
    },
  });

  // Update school mutation
  const updateSchoolMutation = useMutation({
    mutationFn: ({ schoolId, schoolData }: { schoolId: number; schoolData: SchoolUpdateRequest }) =>
      educationService.updateSchool(schoolId, schoolData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education', 'schools'] });
      ToastService.showSuccess('School updated successfully!');
    },
    onError: (error: any) => {
      console.error('Error updating school:', error);
      ToastService.showError('Failed to update school. Please try again.');
    },
  });

  // Delete school mutation
  const deleteSchoolMutation = useMutation({
    mutationFn: (schoolId: number) => educationService.deleteSchool(schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education', 'schools'] });
      ToastService.showSuccess('School deleted successfully!');
    },
    onError: (error: any) => {
      console.error('Error deleting school:', error);
      ToastService.showError('Failed to delete school. Please try again.');
    },
  });

  // Add course mutation
  const addCourseMutation = useMutation({
    mutationFn: ({ schoolId, courseData }: { schoolId: number; courseData: CourseCreateRequest }) =>
      educationService.addCourseToSchool(schoolId, courseData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education', 'schools'] });
      ToastService.showSuccess('Course added successfully!');
    },
    onError: (error: any) => {
      console.error('Error adding course:', error);
      ToastService.showError('Failed to add course. Please try again.');
    },
  });

  // Update course mutation
  const updateCourseMutation = useMutation({
    mutationFn: ({ courseId, courseData }: { courseId: number; courseData: CourseUpdateRequest }) =>
      educationService.updateCourse(courseId, courseData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education', 'schools'] });
      ToastService.showSuccess('Course updated successfully!');
    },
    onError: (error: any) => {
      console.error('Error updating course:', error);
      ToastService.showError('Failed to update course. Please try again.');
    },
  });

  // Delete course mutation
  const deleteCourseMutation = useMutation({
    mutationFn: (courseId: number) => educationService.deleteCourse(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education', 'schools'] });
      ToastService.showSuccess('Course deleted successfully!');
    },
    onError: (error: any) => {
      console.error('Error deleting course:', error);
      ToastService.showError('Failed to delete course. Please try again.');
    },
  });

  // Analyze course mutation
  const analyzeCourseMutation = useMutation({
    mutationFn: (request: CourseAnalysisRequest) => educationService.analyzeCourse(request),
    onError: (error: any) => {
      console.error('Error analyzing course:', error);
      ToastService.showError('Failed to analyze course. Please try again.');
    },
  });

  // Helper functions
  const createSchool = (schoolData: SchoolCreateRequest) => {
    return createSchoolMutation.mutateAsync(schoolData);
  };

  const updateSchool = (schoolId: number, schoolData: SchoolUpdateRequest) => {
    return updateSchoolMutation.mutateAsync({ schoolId, schoolData });
  };

  const deleteSchool = (schoolId: number) => {
    return deleteSchoolMutation.mutateAsync(schoolId);
  };

  const addCourse = (schoolId: number, courseData: CourseCreateRequest) => {
    return addCourseMutation.mutateAsync({ schoolId, courseData });
  };

  const updateCourse = (courseId: number, courseData: CourseUpdateRequest) => {
    return updateCourseMutation.mutateAsync({ courseId, courseData });
  };

  const deleteCourse = (courseId: number) => {
    return deleteCourseMutation.mutateAsync(courseId);
  };

  const analyzeCourse = (courseName: string) => {
    return analyzeCourseMutation.mutateAsync({ course_name: courseName });
  };

  return {
    // Data
    schools: schoolsData?.schools || [],
    totalCount: schoolsData?.total_count || 0,
    
    // Loading states
    isLoading,
    isCreatingSchool: createSchoolMutation.isPending,
    isUpdatingSchool: updateSchoolMutation.isPending,
    isDeletingSchool: deleteSchoolMutation.isPending,
    isAddingCourse: addCourseMutation.isPending,
    isUpdatingCourse: updateCourseMutation.isPending,
    isDeletingCourse: deleteCourseMutation.isPending,
    isAnalyzingCourse: analyzeCourseMutation.isPending,
    
    // Error states
    error,
    
    // Actions
    createSchool,
    updateSchool,
    deleteSchool,
    addCourse,
    updateCourse,
    deleteCourse,
    analyzeCourse,
    refetch,
    
    // Analysis result
    courseAnalysisResult: analyzeCourseMutation.data,
  };
};